import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCartStore from '../../context/cartStore';

const CartSidebar = () => {
  const { items, isOpen, closeCart, removeItem, updateQuantity } = useCartStore();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const currency = isAr ? 'د.ك' : 'KD';
  const formatPrice = (n) => isAr ? `${n.toFixed(3)} ${currency}` : `${currency} ${n.toFixed(3)}`;

  return (
    <>
      {isOpen && <div className="cart-overlay" onClick={closeCart} />}
      <aside className={`cart-sidebar ${isOpen ? 'cart-sidebar--open' : ''}`} dir={isAr ? 'rtl' : 'ltr'}>
        <div className="cart-sidebar__header">
          <h3>{t('cart.title')}</h3>
          <button onClick={closeCart} className="cart-sidebar__close">✕</button>
        </div>

        {items.length === 0 ? (
          <div className="cart-sidebar__empty">
            <div className="cart-sidebar__empty-icon">🍯</div>
            <p>{t('cart.empty')}</p>
            <button className="btn btn-primary" onClick={closeCart}>{t('cart.continueShopping')}</button>
          </div>
        ) : (
          <>
            <div className="cart-sidebar__items">
              {items.map(item => (
                <div key={item.key} className="cart-item">
                  <div className="cart-item__img">
                    {item.image ? <img src={item.image} alt={item.name} /> : <span>🍯</span>}
                  </div>
                  <div className="cart-item__info">
                    <p className="cart-item__name">{item.name}</p>
                    {item.variant_label && <p className="cart-item__variant">{item.variant_label}</p>}
                    <p className="cart-item__price">{formatPrice(item.price)}</p>
                    <div className="cart-item__qty">
                      <button onClick={() => updateQuantity(item.key, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.key, item.quantity + 1)}>+</button>
                    </div>
                  </div>
                  <button className="cart-item__remove" onClick={() => removeItem(item.key)} aria-label={t('cart.remove')}>✕</button>
                </div>
              ))}
            </div>

            <div className="cart-sidebar__footer">
              <div className="cart-sidebar__subtotal">
                <span>{t('cart.subtotal')}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <p className="cart-sidebar__shipping-note">
                {subtotal >= 10
                  ? `✅ ${t('cart.freeShipping')}`
                  : t('cart.addForFreeShipping', { amount: (10 - subtotal).toFixed(3) })}
              </p>
              <Link to="/checkout" className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={closeCart}>
                {t('cart.checkout')}
              </Link>
              <button className="cart-sidebar__continue" onClick={closeCart}>{t('cart.continueShopping')}</button>
            </div>
          </>
        )}
      </aside>

      <style>{`
        .cart-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1099; backdrop-filter: blur(2px);
        }
        .cart-sidebar {
          position: fixed; top: 0; right: 0; width: 400px; height: 100vh;
          background: var(--white); z-index: 1100; display: flex; flex-direction: column;
          transform: translateX(100%); transition: transform 0.35s cubic-bezier(.4,0,.2,1);
          box-shadow: -8px 0 40px rgba(44,36,22,0.15);
        }
        [dir="rtl"] .cart-sidebar {
          right: auto; left: 0;
          transform: translateX(-100%);
          box-shadow: 8px 0 40px rgba(44,36,22,0.15);
        }
        .cart-sidebar--open { transform: translateX(0) !important; }
        .cart-sidebar__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 24px; border-bottom: 1px solid var(--border);
        }
        .cart-sidebar__header h3 { font-family: var(--font-serif); font-size: 1.3rem; }
        .cart-sidebar__close { font-size: 1.2rem; color: var(--text-muted); }
        .cart-sidebar__empty {
          flex: 1; display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: 16px; padding: 40px;
        }
        .cart-sidebar__empty-icon { font-size: 3rem; }
        .cart-sidebar__empty p { color: var(--text-muted); }
        .cart-sidebar__items { flex: 1; overflow-y: auto; padding: 16px 24px; display: flex; flex-direction: column; gap: 16px; }
        .cart-item { display: flex; gap: 14px; align-items: flex-start; }
        .cart-item__img {
          width: 72px; height: 72px; border-radius: 10px; background: var(--cream);
          overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 1.5rem;
        }
        .cart-item__img img { width: 100%; height: 100%; object-fit: cover; }
        .cart-item__info { flex: 1; }
        .cart-item__name { font-weight: 600; font-size: 0.9rem; }
        .cart-item__variant { font-size: 0.78rem; color: var(--text-muted); }
        .cart-item__price { font-weight: 700; color: var(--gold); font-size: 0.9rem; margin: 4px 0; }
        .cart-item__qty {
          display: flex; align-items: center; gap: 10px; margin-top: 8px;
        }
        .cart-item__qty button {
          width: 26px; height: 26px; border-radius: 50%; background: var(--cream);
          font-size: 1rem; display: flex; align-items: center; justify-content: center;
          font-weight: 700; transition: background 0.2s;
        }
        .cart-item__qty button:hover { background: var(--gold); color: white; }
        .cart-item__qty span { font-weight: 600; min-width: 20px; text-align: center; }
        .cart-item__remove { color: var(--text-muted); font-size: 0.8rem; margin-top: 2px; }
        .cart-sidebar__footer { padding: 20px 24px; border-top: 1px solid var(--border); }
        .cart-sidebar__subtotal {
          display: flex; justify-content: space-between; font-weight: 700; font-size: 1.05rem; margin-bottom: 8px;
        }
        .cart-sidebar__shipping-note { font-size: 0.8rem; color: var(--olive); }
        .cart-sidebar__continue {
          width: 100%; text-align: center; margin-top: 12px; color: var(--text-muted);
          font-size: 0.85rem; text-decoration: underline;
        }
        @media (max-width: 480px) { .cart-sidebar { width: 100%; } }
      `}</style>
    </>
  );
};

export default CartSidebar;
